import { Inertia } from '@inertiajs/inertia'
import { computed, reactive, ref } from 'vue'
import { roleOn } from '../Calendar/pilotColors'

/**
 * Drag state and requests for putting pilots into a leg's PIC or SIC slot,
 * shared by the crew grid on the trip page and the calendar. The payload lives
 * here rather than in dataTransfer, which can't be read during dragover (when
 * slots decide whether to highlight).
 *
 * canDrop(leg, pilot) lets a page add its own rule, such as the calendar only
 * accepting pilots of the flight's plane.
 */
export function useCrewDrag({ canDrop = () => true } = {}) {
  const drag = ref(null) // { pilot, fromLeg }

  // dragenter and dragleave fire for every child element, so count them per target
  const overCounts = reactive({})

  function startDrag(pilot, fromLeg, event) {
    drag.value = { pilot, fromLeg }

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      // Firefox won't start a drag without some data
      event.dataTransfer.setData('text/plain', pilot.name)
    }
  }

  function endDrag() {
    drag.value = null

    for (const key of Object.keys(overCounts)) delete overCounts[key]
  }

  function enter(key) {
    overCounts[key] = (overCounts[key] || 0) + 1
  }

  function leave(key) {
    if (overCounts[key] > 0) overCounts[key]--
  }

  function isOver(key) {
    return overCounts[key] > 0
  }

  function allowMove(event) {
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  }

  function slotAccepts(leg, role) {
    if (!drag.value || !canDrop(leg, drag.value.pilot)) return false

    // Dropping a chip back where it came from is a no-op
    return !(
      drag.value.fromLeg?.id === leg.id &&
      roleOn(leg, drag.value.pilot) === role
    )
  }

  // A chip dragged out of a slot can be dropped on the strip to unassign it
  const isRemoveZone = computed(() => !!drag.value?.fromLeg)

  // Requests. Every drop is one request; the page re-renders from the response.

  function assign(leg, role, pilot, fromLeg = null) {
    Inertia.post(
      route('trips.legs.crew.store', { trip: leg.trip_id, leg: leg.id }),
      { user: { id: pilot.id }, role, from_leg_id: fromLeg?.id ?? null },
      { preserveScroll: true }
    )
  }

  function unassign(leg, pilot) {
    Inertia.delete(
      route('trips.legs.crew.destroy', {
        trip: leg.trip_id,
        leg: leg.id,
        user: pilot.id,
      }),
      { preserveScroll: true }
    )
  }

  function dropOnSlot(leg, role) {
    const current = drag.value
    const accepts = slotAccepts(leg, role)

    endDrag()

    if (accepts) assign(leg, role, current.pilot, current.fromLeg)
  }

  function dropOnRemoveZone() {
    const current = drag.value
    const remove = isRemoveZone.value

    endDrag()

    if (remove) unassign(current.fromLeg, current.pilot)
  }

  return {
    drag,
    startDrag,
    endDrag,
    enter,
    leave,
    isOver,
    allowMove,
    slotAccepts,
    isRemoveZone,
    assign,
    unassign,
    dropOnSlot,
    dropOnRemoveZone,
  }
}
